import React, { useEffect } from "react";
import "./WidgetsDropdown.css";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { CRow, CCol, CDropdown, CWidgetStatsA } from "@coreui/react";
import { CChart } from "@coreui/react-chartjs";
import CIcon from "@coreui/icons-react";
import { cilOptions } from "@coreui/icons";
import { GetAllDashboard } from "../../../../features/admin/dashboard/slice";
import { Link } from "react-router-dom";
import { IDS } from "../../../../utils/IDS";

const WidgetsDropdown = (props) => {
  const dispatch = useDispatch();
  const dashboard = useSelector((state) => state.dashboard.dashboard.data);
  const error = useSelector((state) => state.dashboard.error);
  const userRole = useSelector((state) => state.auth);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    await dispatch(GetAllDashboard({}));
  };

  if (error) {
    return (
      <div className="alert alert-danger">
        Failed to load dashboard: {error}
      </div>
    );
  }

  // Chart.js configuration for the line chart
  const chartConfig = {
    type: "line",
    data: {
      labels: dashboard?.last_month_users?.labels ?? [],
      datasets: [
        {
          label: "Users Added",
          data: dashboard?.last_month_users?.counts ?? [],
          borderColor: "#4e73df",
          backgroundColor: "rgba(78, 115, 223, 0.1)",
          fill: true,
          tension: 0.4,
          borderWidth: 2,
        },
        {
          label: "Builders Added",
          data: dashboard?.last_month_builders?.counts ?? [],
          borderColor: "#1cc88a",
          backgroundColor: "rgba(28, 200, 138, 0.1)",
          fill: true,
          tension: 0.4,
          borderWidth: 2,
        },
        {
          label: "Inactive Users",
          data: dashboard?.last_month_inactive_users?.counts ?? [],
          borderColor: "#e74a3b",
          backgroundColor: "rgba(231, 74, 59, 0.1)",
          fill: true,
          tension: 0.4,
          borderWidth: 2,
        },
        {
          label: "User Subscriptions",
          data: dashboard?.last_month_user_subscriptions?.counts ?? [],
          borderColor: "#f6c23e",
          backgroundColor: "rgba(246, 194, 62, 0.1)",
          fill: true,
          tension: 0.4,
          borderWidth: 2,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          title: {
            display: true,
            text: "Date (DD-MM-YYYY)",
            font: {
              size: 14,
            },
          },
          ticks: {
            maxTicksLimit: 5,
            maxRotation: 45,
            minRotation: 45,
            font: {
              size: 12,
            },
          },
        },
        y: {
          title: {
            display: true,
            text: "Count",
            font: {
              size: 14,
            },
          },
          beginAtZero: true,
          ticks: {
            font: {
              size: 12,
            },
          },
        },
      },
      plugins: {
        legend: {
          display: true,
          position: "top",
          labels: {
            font: {
              size: 14,
            },
          },
        },
        tooltip: {
          mode: "index",
          intersect: false,
          titleFont: {
            size: 14,
          },
          bodyFont: {
            size: 12,
          },
        },
      },
    },
  };

  return (
    <section className="dashboard_holder">
      <CRow className={props.className} xs={{ gutter: 4 }}>
        {userRole?.role === IDS.usersrole.admin && (
          <>
            <CCol sm={6} xl={4} xxl={3}>
              <Link to="/user-management/users">
                <CWidgetStatsA
                  className="pb-3"
                  color="primary"
                  value={
                    <span
                      style={{
                        fontSize: "24px",
                        color: "#fff",
                        textDecoration: "none",
                      }}
                    >
                      {dashboard?.total_users ?? 0}
                    </span>
                  }
                  title="Users"
                  action={<CDropdown alignment="end"></CDropdown>}
                />
              </Link>
            </CCol>
            <CCol sm={6} xl={4} xxl={3}>
              <Link to="/user-management/builder">
                <CWidgetStatsA
                  className="pb-3"
                  color="info"
                  value={
                    <span
                      style={{
                        fontSize: "24px",
                        color: "#fff",
                        textDecoration: "none",
                      }}
                    >
                      {dashboard?.total_builders ?? 0}
                    </span>
                  }
                  title="Builders"
                  action={<CDropdown alignment="end"></CDropdown>}
                />
              </Link>
            </CCol>
            <CCol sm={6} xl={4} xxl={3}>
              <Link to="/coupon-management/usersubscription">
                <CWidgetStatsA
                  className="pb-3"
                  color="warning"
                  value={
                    <span
                      style={{
                        fontSize: "24px",
                        color: "#fff",
                        textDecoration: "none",
                      }}
                    >
                      {dashboard?.total_user_subscriptions ?? 0}
                    </span>
                  }
                  title="User Subscriptions"
                  action={<CDropdown alignment="end"></CDropdown>}
                />
              </Link>
            </CCol>
          </>
        )}

        {/* Widgets for both admin (role_id: 1) and builder (role_id: 2) */}
        <CCol sm={6} xl={4} xxl={3}>
          <Link to="/property-lisiting">
            <CWidgetStatsA
              className="pb-3"
              color="success"
              value={
                <span
                  style={{
                    fontSize: "24px",
                    color: "#fff",
                    textDecoration: "none",
                  }}
                >
                  {dashboard?.total_properties ?? 0}
                </span>
              }
              title="Properties"
              action={<CDropdown alignment="end"></CDropdown>}
            />
          </Link>
        </CCol>
        <CCol sm={6} xl={4} xxl={3}>
          <Link to="/explore-property/appointmentbooking">
            <CWidgetStatsA
              className="pb-3"
              color="warning"
              value={
                <span
                  style={{
                    fontSize: "24px",
                    color: "#fff",
                    textDecoration: "none",
                  }}
                >
                  {dashboard?.total_appointment_bookings ?? 0}
                </span>
              }
              title="Appointment Bookings"
              action={<CDropdown alignment="end"></CDropdown>}
            />
          </Link>
        </CCol>
        <CCol sm={6} xl={4} xxl={3}>
          <Link to="/best-property/bestpriceproperty">
            <CWidgetStatsA
              className="pb-3"
              color="danger"
              value={
                <span
                  style={{
                    fontSize: "24px",
                    color: "#fff",
                    textDecoration: "none",
                  }}
                >
                  {dashboard?.total_best_price_properties ?? 0}
                </span>
              }
              title="Best Price Properties"
              action={<CDropdown alignment="end"></CDropdown>}
            />
          </Link>
        </CCol>

        {/* Admin-only widgets (role_id: 1) continued */}
        {userRole?.role === IDS.usersrole.admin && (
          <>
            <CCol sm={6} xl={4} xxl={3}>
              <Link to="/coupon-management/subscription">
                <CWidgetStatsA
                  className="pb-3"
                  color="primary"
                  value={
                    <span
                      style={{
                        fontSize: "24px",
                        color: "#fff",
                        textDecoration: "none",
                      }}
                    >
                      {dashboard?.total_subscriptions ?? 0}
                    </span>
                  }
                  title="Subscriptions"
                  action={<CDropdown alignment="end"></CDropdown>}
                />
              </Link>
            </CCol>
            <CCol sm={6} xl={4} xxl={3}>
              <Link to="/coupon-management/coupon">
                <CWidgetStatsA
                  className="pb-3"
                  color="info"
                  value={
                    <span
                      style={{
                        fontSize: "24px",
                        color: "#fff",
                        textDecoration: "none",
                      }}
                    >
                      {dashboard?.total_coupons ?? 0}
                    </span>
                  }
                  title="Coupons"
                  action={<CDropdown alignment="end"></CDropdown>}
                />
              </Link>
            </CCol>
            <CCol sm={6} xl={4} xxl={3}>
              <Link to="/property-inspection/inspection">
                <CWidgetStatsA
                  className="pb-3"
                  color="primary"
                  value={
                    <span
                      style={{
                        fontSize: "24px",
                        color: "#fff",
                        textDecoration: "none",
                      }}
                    >
                      {dashboard?.total_reinspections ?? 0}
                    </span>
                  }
                  title="Inspection Form"
                  action={<CDropdown alignment="end"></CDropdown>}
                />
              </Link>
            </CCol>
            <CCol sm={6} xl={4} xxl={3}>
              <Link to="/ticket-raised/ticket">
                <CWidgetStatsA
                  className="pb-3"
                  color="dark"
                  value={
                    <span
                      style={{
                        fontSize: "24px",
                        color: "#fff",
                        textDecoration: "none",
                      }}
                    >
                      {dashboard?.total_tickets ?? 0}
                    </span>
                  }
                  title="Ticket Types"
                  action={<CDropdown alignment="end"></CDropdown>}
                />
              </Link>
            </CCol>
            <CCol sm={6} xl={4} xxl={3}>
              <Link to="/wallet-management/wallethistory?name=admin">
                <CWidgetStatsA
                  className="pb-3"
                  color="success"
                  value={
                    <span
                      style={{
                        fontSize: "24px",
                        color: "#fff",
                        textDecoration: "none",
                      }}
                    >
                      ₹{dashboard?.total_admin_wallet?.toFixed(2) ?? "0.00"}
                    </span>
                  }
                  title="Admin Wallet"
                  action={<CDropdown alignment="end"></CDropdown>}
                />
              </Link>
            </CCol>
          </>
        )}
      </CRow>
      <CRow>
        {/* Line Chart for Last Month's Users, Builders, Inactive Users, and User Subscriptions (Admin only) */}
        {userRole?.role === IDS.usersrole.admin && (
          <CCol xs={6}>
            <h3>
              Last Month's User Count, Builder Count, Inactive User Count, and
              User Subscription Count Additions
            </h3>
            <div style={{ height: "600px" }}>
              <CChart
                type="line"
                data={chartConfig.data}
                options={chartConfig.options}
              />
            </div>
          </CCol>
        )}
      </CRow>
    </section>
  );
};

WidgetsDropdown.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

export default WidgetsDropdown;
